import { produce } from 'immer';
import moment from 'moment';
import {
  JSX,
  createContext,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Http } from '../../../common/ajax';
import { customEvents } from '../../../common/constants';
import { config } from '../../../common/global/config';
import useMount from '../../../common/hooks/useMount';
import { setTraceUser } from '../../../common/telemetry/logTrace';
import { contestService } from '../services/contest';
import { customerService } from '../services/customer';
import { ICustomerInfoContext } from './common';

interface CustomerInfoContextValue {
  customerInfo: ICustomerInfoContext;
  setCustomerInfo: (customerInfo: ICustomerInfoContext) => void;
  loadCustomerInfo: (abortController?: AbortController) => Promise<void>;
  isLoading: boolean;
}

export const CustomerInfoContext = createContext<CustomerInfoContextValue>(
  {} as CustomerInfoContextValue,
);

export function CustomerInfoProvider(props: { children: JSX.Element }) {
  const [customerInfo, setCustomerInfo] = useState({} as ICustomerInfoContext);
  const [isLoading, setIsLoading] = useState(true);

  // Http needs access to the latest language without recreating the getter.
  const customerInfoRef = useRef<ICustomerInfoContext>(customerInfo);
  customerInfoRef.current = customerInfo;

  useMemo(() => {
    Http.setLanguageGetter(() => {
      return customerInfoRef.current?.languageCode ?? config.defaultLanguage;
    });
  }, []);

  const loadCustomerInfo = useCallback(
    async (abortController?: AbortController) => {
      setIsLoading(true);

      try {
        const result = await customerService.getWhoAmI({
          signal: abortController?.signal,
        });

        const contestsResult = await contestService.getContests({
          signal: abortController?.signal,
        });

        if (abortController?.signal.aborted) {
          return;
        }

        if (!result.hasErrors) {
          setCustomerInfo(
            produce(draft => {
              draft.clientId = result.data.clientId;
              draft.customerGroupId = result.data.customerGroupId;
              draft.email = result.data.email;
              draft.firstName = result.data.firstName;
              draft.lastName = result.data.lastName;
              draft.fullName = result.data.fullName;
              draft.notificationsCount = result.data.notificationsCount;
              draft.cartItemsCount = result.data.cartItemsCount;
              draft.favoriteProductsCount = result.data.favoriteProductsCount;
              draft.isPowerUser = result.data.isPowerUser;
              draft.isGrossUpUser = result.data.isGrossUpUser;
              draft.isSavingsUser = result.data.isSavingsUser ?? false;
              draft.isImpersonating = result.data.isImpersonating;
              draft.platformType = result.data.platformType;
              draft.acceptedTcAndPp = result.data.acceptedTcAndPp;
              draft.canAccessFlexibleBenefits =
                result.data.canAccessFlexibleBenefits;
              draft.canAccessDiscountVouchers =
                result.data.canAccessDiscountVouchers;
              draft.canAccessCashback = result.data.canAccessCashback;
              draft.canAccessBenefitClub = result.data.canAccessBenefitClub;
              draft.canAccessMyBenefits = result.data.canAccessMyBenefits;
              draft.canAccessMyCards = result.data.canAccessMyCards;
              draft.canAccessTimeAndAttendanceModule =
                result.data.canAccessTimeAndAttendanceModule;
              draft.canGenerateDemoAccount = result.data.canGenerateDemoAccount;
              draft.canAccessExclusive = result.data.canAccessExclusive;
              draft.canAccessSubscriptions = result.data.canAccessSubscriptions;
              draft.showWallet = result.data.showWallet;
              draft.allowsDy = result.data.allowsDy;
              draft.languageCode = result.data.languageCode;
              draft.showNewsletterBanners = result.data.showNewsletterBanners;
              draft.showQuizBanners = result.data.showQuizBanners;
              draft.showNewsletterPopup = result.data.showNewsletterPopup;
              draft.showDyConsentPopup = result.data.showDyConsentPopup;
              draft.hasDynamicYieldConsent = result.data.hasDynamicYieldConsent;
              draft.showNewsletterPreferences =
                result.data.showNewsletterPreferences;

              if (!contestsResult?.hasErrors) {
                draft.contests = contestsResult.data;
              }
            }),
          );

          setTraceUser({
            email: result.data.email,
            clientId: result.data.clientId,
          });
          moment.locale(result.data.languageCode);
        }
      } finally {
        if (!abortController?.signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [],
  );

  useMount(abortController => {
    loadCustomerInfo(abortController);

    const refreshCustomerInfo = () => {
      loadCustomerInfo(abortController);
    };

    document.addEventListener(
      customEvents.refreshCustomerBalances,
      refreshCustomerInfo,
    );

    return () => {
      document.removeEventListener(
        customEvents.refreshCustomerBalances,
        refreshCustomerInfo,
      );
    };
  });

  const value = useMemo(
    () => ({
      customerInfo,
      setCustomerInfo,
      loadCustomerInfo,
      isLoading,
    }),
    [customerInfo, loadCustomerInfo, isLoading],
  );

  return (
    <CustomerInfoContext.Provider value={value}>
      {props.children}
    </CustomerInfoContext.Provider>
  );
}
